import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import {
  Grid, IconButton, Slide,
} from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';
import NavigateNext from '@mui/icons-material/NavigateNext';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';
import { useMobile } from '@gamiphy/loyalty-station-desktop/utils/mobile';
import TierBenefits from './components/TierBenefits';
import { NavDirections } from './enums';

const Benefits = observer(() => {
  const isMobile = useMobile();
  const theme = useTheme();
  const [activeTier, setActiveTier] = React.useState<number>(LevelStore.currentTier?.order ?? 0);
  const [navDirection, setNavDirection] = React.useState<NavDirections>(NavDirections.left);
  const [slideIn, setSlideIn] = React.useState<boolean>(true);

  const [touchStart, setTouchStart] = React.useState<number | null>(0);
  const [touchEnd, setTouchEnd] = React.useState<number | null>(0);

  const navHandler = React.useCallback((direction: NavDirections) => {
    const increment = direction === NavDirections.left ? -1 : 1;
    const newActiveTier = activeTier + increment;
    const oppDirection: NavDirections = direction === NavDirections.left ? NavDirections.right : NavDirections.left;
    setNavDirection(direction);
    setSlideIn(false);
    setTimeout(() => {
      setActiveTier(newActiveTier);
      setNavDirection(oppDirection);
      setSlideIn(true);
    }, 200);
  }, [activeTier]);

  useEffect(() => {
    setActiveTier(LevelStore.currentTier?.order ?? 0);
  }, [LevelStore.currentTier?.order]);

  const onTouchStart = React.useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const onTouchMove = React.useCallback((e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX), []);

  const onTouchEnd = React.useCallback(() => {
    if (!touchStart || !touchEnd) {
      return;
    }
    const minSwipeDistance = 50;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe && activeTier < (LevelStore.tiers?.length ?? 0) - 1) {
      navHandler(NavDirections.right);
    }
    if (isRightSwipe && activeTier > 0) {
      navHandler(NavDirections.left);
    }
  }, [touchStart, touchEnd]);

  return (
    <Grid
      container
      item
      position="relative"
      xs={(LevelStore.tiers?.length ?? 0) > 1 ? 10 : 12}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {activeTier > 0 && (
      <IconButton
        sx={{
          padding: '0.2rem',
          boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
          backgroundColor: '#fff',
          position: 'absolute',
          left: -20,
          top: '50%',
          transform: 'translateY(-50%)',
          '&:hover': {
            backgroundColor: !isMobile ? 'primary.main' : 'unset',
          },
          '&:active': {
            backgroundColor: isMobile ? 'primary.main' : 'unset',
          },
        }}
        onClick={() => navHandler(NavDirections.left)}
      >
        {theme.direction === 'rtl' ? <NavigateNext sx={{ fontSize: 20 }} /> : <NavigateBefore sx={{ fontSize: 20 }} />}
      </IconButton>
      )}
      <Grid container flexWrap="nowrap" justifyContent="center" alignItems="center">
        {LevelStore.tiers?.map((tier) => (
          <Slide
            key={tier.id}
            direction={navDirection === NavDirections.left ? 'left' : 'right'}
            in={activeTier === tier.order && slideIn}
            timeout={200}
            unmountOnExit
          >
            <Grid item flexShrink={0} sx={{ width: '100%' }}>
              <TierBenefits tier={tier} />
            </Grid>
          </Slide>
        ))}
      </Grid>
      {activeTier < (LevelStore.tiers?.length ?? 0) - 1 && (
      <IconButton
        sx={{
          padding: '0.2rem',
          boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
          backgroundColor: '#fff',
          position: 'absolute',
          right: -20,
          top: '50%',
          transform: 'translateY(-50%)',
          '&:hover': {
            backgroundColor: !isMobile ? 'primary.main' : 'unset',
          },
          '&:active': {
            backgroundColor: isMobile ? 'primary.main' : 'unset',
          },
        }}
        onClick={() => navHandler(NavDirections.right)}
      >
        {theme.direction === 'rtl' ? <NavigateBefore sx={{ fontSize: 20 }} /> : <NavigateNext sx={{ fontSize: 20 }} />}
      </IconButton>
      )}
    </Grid>
  );
});

export default Benefits;
